/**
 * MaterialEstadoBadge — chip visual con el estado de cada material en la OT.
 *
 * Muestra el ciclo de vida del material (pendiente_compra → pedido_proveedor →
 * recibido) con link a la orden de compra y/o factura cuando aplica.
 *
 * Estados reconocidos (campo `estado_material`):
 *   - recibido            ✅ verde   — material en stock al añadirse o llegado por factura
 *   - pendiente_compra    🟡 amber   — esperando ser aprobado/pedido
 *   - compra_aprobada     🔵 blue    — admin aprobó, falta enviar al proveedor
 *   - pedido_proveedor    🟣 violet  — pedido al proveedor, esperando recepción
 *   - cancelado           ⚫ gris    — descartado por admin
 *   - (sin estado / null) — no se muestra badge (materiales antiguos)
 */
import { Badge } from './ui/badge';
import { CheckCircle2, Clock, ShoppingCart, Truck, XCircle, FileText, Hash } from 'lucide-react';

const CONFIG = {
  recibido: {
    label: 'Recibido', icon: CheckCircle2,
    className: 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-100',
  },
  pendiente_compra: {
    label: 'Pendiente compra', icon: Clock,
    className: 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-100',
  },
  compra_aprobada: {
    label: 'Compra aprobada', icon: ShoppingCart,
    className: 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-100',
  },
  pedido_proveedor: {
    label: 'Pedido al proveedor', icon: Truck,
    className: 'bg-violet-100 text-violet-700 border-violet-300 hover:bg-violet-100',
  },
  cancelado: {
    label: 'Cancelado', icon: XCircle,
    className: 'bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-100',
  },
};

export default function MaterialEstadoBadge({ material, compact = false }) {
  const estado = material?.estado_material;
  if (!estado || !CONFIG[estado]) return null;
  const { label, icon: Icon, className } = CONFIG[estado];

  return (
    <div className="inline-flex flex-wrap items-center gap-1" data-testid={`material-estado-${estado}`}>
      <Badge variant="outline" className={`${className} text-[10px] gap-1 px-1.5 py-0`}>
        <Icon className="w-3 h-3" />
        {compact ? label.split(' ')[0] : label}
      </Badge>

      {/* Link a la factura/compra si está recibido */}
      {estado === 'recibido' && material.compra_id && (
        <a
          href={`/crm/compras/${material.compra_id}`}
          className="text-[10px] inline-flex items-center gap-0.5 text-emerald-700 hover:underline"
          title="Ver factura de compra"
          data-testid="link-factura"
        >
          <FileText className="w-3 h-3" />
          {material.numero_factura || 'Factura'}
        </a>
      )}

      {/* Código de lote si hay */}
      {material.codigo_lote && estado === 'recibido' && (
        <span
          className="text-[10px] inline-flex items-center gap-0.5 font-mono text-slate-500"
          title="Código de lote"
        >
          <Hash className="w-2.5 h-2.5" />
          {material.codigo_lote.slice(0, 12)}
        </span>
      )}

      {/* Link a la OC si está en proceso */}
      {(estado === 'compra_aprobada' || estado === 'pedido_proveedor') && material.orden_compra_id && (
        <a
          href={`/crm/ordenes-compra/${material.orden_compra_id}`}
          className="text-[10px] inline-flex items-center gap-0.5 text-blue-700 hover:underline"
          title="Ver orden de compra"
          data-testid="link-oc"
        >
          <ShoppingCart className="w-3 h-3" />
          OC
        </a>
      )}

      {/* Link a la lista de compras si está pendiente */}
      {estado === 'pendiente_compra' && material.lista_compras_id && (
        <a
          href="/crm/compras/lista"
          className="text-[10px] inline-flex items-center gap-0.5 text-amber-700 hover:underline"
          title="Ver en lista de compras"
          data-testid="link-lista-compras"
        >
          <ShoppingCart className="w-3 h-3" />
          Lista
        </a>
      )}
    </div>
  );
}
